import { Sparkles, Bot } from 'lucide-react';
import { useAIChat } from '../../config/aiChatContext';

/**
 * MarketHero — Cyber-Luxe hero shared by all marketplace category pages.
 * Dark/light aware: sky-blue + warm-gold orbs, AI dot grid, brand scanline.
 */
export default function MarketHero({
  isDarkMode,
  isAr,
  eyebrowAr,
  eyebrowEn,
  titleAr,
  titleAccentAr,
  titleEn,
  titleAccentEn,
  subtitleAr,
  subtitleEn,
  icon: Icon,
  accentA = '#1E9BD9',
  stats = [],
  aiPromptAr,
  aiPromptEn,
}) {
  const { openChat } = useAIChat();
  const dark = isDarkMode;
  const textMain = dark ? 'text-white' : 'text-[#0F172A]';
  const textMuted = dark ? 'text-slate-400' : 'text-slate-600';
  const glass = dark ? 'cgm-glass-dark' : 'cgm-glass-light';
  const aiPrompt = isAr ? aiPromptAr : aiPromptEn;

  const StatPill = ({ s, i }) => (
    <div key={i} className={`rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 ${glass} cgm-rise`} style={{ animationDelay: `${150 + i * 80}ms` }} data-testid={`market-hero-stat-${i}`}>
      <div className="flex items-center gap-1.5">
        {s.icon && <span style={{ color: s.accent || accentA }}>{s.icon}</span>}
        <span className={`text-lg sm:text-xl font-black leading-tight tabular-nums ${textMain}`}>{s.value}</span>
      </div>
      <span className={`text-[10px] sm:text-[11px] font-medium ${textMuted}`}>{s.label}</span>
    </div>
  );

  return (
    <section className="relative overflow-hidden" data-testid="market-hero">
      <div className={`absolute inset-0 ${dark ? 'bg-[#0A0B10]' : 'bg-gradient-to-b from-white to-[#F8FAFC]'}`} />
      <div className={`absolute inset-0 ${dark ? 'cgm-dot-grid opacity-40' : 'cgm-dot-grid-light opacity-50'} [mask-image:radial-gradient(ellipse_at_top,black_25%,transparent_75%)]`} />
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute -top-32 start-[4%] w-[480px] h-[480px] rounded-full blur-[130px] ${dark ? 'bg-[#1E9BD9]/20' : 'bg-[#1E9BD9]/15'}`} />
        <div className={`absolute -bottom-40 end-[6%] w-[400px] h-[400px] rounded-full blur-[120px] ${dark ? 'bg-[#F5A962]/15' : 'bg-[#F5A962]/20'}`} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-16 sm:pb-20">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 space-y-5 animate-fadeIn">
            <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border ${dark ? 'bg-[#1E9BD9]/10 border-[#1E9BD9]/30' : 'bg-white border-[#1E9BD9]/30 shadow-sm'}`} data-testid="market-hero-eyebrow">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <Sparkles className="h-3.5 w-3.5 text-[#1E9BD9]" />
              <span className={`text-xs sm:text-sm font-semibold ${dark ? 'cgm-brand-text' : 'text-[#0284C7]'}`}>{isAr ? eyebrowAr : eyebrowEn}</span>
            </div>

            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] ${textMain}`} data-testid="page-title">
              {isAr ? titleAr : titleEn}{' '}
              <span className="cgm-brand-text pb-1 inline-block">{isAr ? titleAccentAr : titleAccentEn}</span>
            </h1>

            <p className={`text-base sm:text-lg max-w-2xl leading-relaxed ${textMuted}`}>{isAr ? subtitleAr : subtitleEn}</p>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              {aiPrompt && (
                <button
                  type="button"
                  onClick={() => openChat(aiPrompt)}
                  className="h-11 px-5 rounded-xl text-white text-sm font-bold inline-flex items-center gap-2 bg-gradient-to-r from-[#1E9BD9] via-[#38BDF8] to-[#F5A962] hover:shadow-[0_8px_24px_rgba(30,155,217,0.45)] hover:-translate-y-0.5 transition-all"
                  data-testid="market-hero-ask-ai"
                >
                  <Bot className="h-4 w-4" />
                  {isAr ? 'اسأل CGM AI عن هذا القسم' : 'Ask CGM AI about this section'}
                </button>
              )}
              <div className="flex gap-2 lg:hidden">
                {stats.map((s, i) => <StatPill key={i} s={s} i={i} />)}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 hidden lg:flex justify-end">
            <div className={`relative w-full max-w-sm rounded-3xl p-6 ${glass} shadow-[0_20px_60px_rgba(30,155,217,0.15)] overflow-hidden`} data-testid="market-hero-panel">
              <div className="absolute inset-x-6 top-0 cgm-scanline" />
              <div className="absolute inset-x-6 h-10 bg-gradient-to-b from-transparent via-[#1E9BD9]/10 to-transparent cgm-scan-beam pointer-events-none" />
              <div className="flex items-center gap-4 mb-5">
                <div className="relative w-16 h-16 shrink-0">
                  <div className="absolute inset-0 rounded-full border border-dashed border-[#1E9BD9]/40 cgm-orbit">
                    <span className="absolute -top-1 left-1/2 w-2 h-2 rounded-full bg-[#F5A962] shadow-[0_0_10px_#F5A962]" />
                  </div>
                  <div className={`absolute inset-2 rounded-full flex items-center justify-center ${dark ? 'bg-[#1E9BD9]/15' : 'bg-[#1E9BD9]/10'}`}>
                    {Icon && <Icon className="h-6 w-6 text-[#1E9BD9]" />}
                  </div>
                </div>
                <div>
                  <div className={`text-sm font-bold ${textMain}`}>{isAr ? 'مفهرَس بالذكاء الاصطناعي' : 'AI-indexed listings'}</div>
                  <div className={`text-xs ${textMuted}`}>{isAr ? 'تحديث مباشر من التجار المعتمدين' : 'Live from verified merchants'}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {stats.map((s, i) => <StatPill key={i} s={s} i={i} />)}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 cgm-scanline opacity-60" />
    </section>
  );
}
